import { useState } from 'react';
import { IconAngleRight } from '@iwoca/icons';
import { GetApiIntroducerPortalIntroducerApplicationOfferDrawDownRepaymentScheduleResponse } from '@iwoca/lapi-client/introducer_portal';
import { Message, Modal } from '@iwoca/orion';
import { Trans, t } from '@lingui/macro';
import { ButtonLink } from 'components/ButtonLink/ButtonLink';
import { ExportRepaymentScheduleToCSVButton } from 'components/ExportRepaymentScheduleToCSVButton/ExportRepaymentScheduleToCSVButton';
import { ExportRepaymentScheduleToPDFButton } from 'components/ExportRepaymentScheduleToPDFButton/ExportRepaymentScheduleToPDFButton';
import { InterestRateMessage } from 'components/InterestRateMessage/InterestRateMessage';
import { RepaymentScheduleTable } from 'components/RepaymentScheduleTable/RepaymentScheduleTable';
import { useAmplitude } from 'hooks/useAmplitude';
import { useLocale } from 'hooks/useLocale';
import { useNumberFormat } from 'hooks/useNumberFormat';
import { usePhoneNumber } from 'hooks/usePhoneNumber';
import { TIntroducerApplication } from 'lendingApi/applications.types';
import { deriveProductFromTypeAndDuration } from 'utils/productHelper';
import { TDrawdownRepaymentQueryParams } from 'utils/tests/urls.config';
import { AMPLITUDE_EVENTS } from 'utils/tracking/tracking.events';
import styles from './EarlyRepaymentSchedule.module.css';

type TProps = {
  noSelectedOffer?: boolean;
  schedule: GetApiIntroducerPortalIntroducerApplicationOfferDrawDownRepaymentScheduleResponse['data']['schedule'];
  summary: GetApiIntroducerPortalIntroducerApplicationOfferDrawDownRepaymentScheduleResponse['data']['summary'];
  formalOffer: TIntroducerApplication['formal_offer_sets'][number]['formal_offers'][number];
  isCustomDate: boolean;
  pdfQuery: TDrawdownRepaymentQueryParams;
};

export const EarlyRepaymentSchedule = ({
  noSelectedOffer = false,
  schedule,
  summary,
  formalOffer,
  isCustomDate,
  pdfQuery,
}: TProps) => {
  const { trackEvent } = useAmplitude();
  const { formatCurrency } = useNumberFormat();
  const { isUK, i18n } = useLocale();
  const { phoneNumber } = usePhoneNumber();
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [hasDownloadError, setHasDownloadError] = useState(false);

  const renderTitle = () => {
    return deriveProductFromTypeAndDuration({
      productType: formalOffer.product_type,
      duration: formalOffer.duration,
      isUK,
    }).name;
  };

  const renderSubTitle = () => {
    const loanAmount = formatCurrency(summary.total_credit_amount);

    return (
      <>
        <div className={styles.subTitleWrapper}>
          <span>
            <Trans>Loan amount: {loanAmount}</Trans>
          </span>
          <div className={styles.exportButtons}>
            <ExportRepaymentScheduleToPDFButton
              pdfQuery={pdfQuery}
              offerId={formalOffer.offer_id}
              displayError={setHasDownloadError}
            />
            <ExportRepaymentScheduleToCSVButton
              schedule={schedule}
              showShortLabel={true}
            />
          </div>
        </div>
        {hasDownloadError && (
          <Message
            message={t`The download failed. Please contact us at ${phoneNumber}.`}
            type="error"
          />
        )}
      </>
    );
  };

  const renderFooter = () => {
    const length = summary.total_number_of_repayments;
    if (!length) {
      return null;
    }
    const savedAmount = formatCurrency(summary.amount_saved || 0);

    const lastPaymentDate = schedule[schedule.length - 1].date;
    const customDate = i18n.date(lastPaymentDate, {
      day: '2-digit',
      month: 'short',
      year: '2-digit',
    });
    const lengthText = isCustomDate
      ? t`by ${customDate}`
      : length > 1
        ? t`in ${length} months`
        : t`in ${length} month`;

    return (
      <div className={styles.tableFooter}>
        <p className={styles.footerText}>
          <Trans>
            Save {savedAmount} by paying off the full loan {lengthText}
          </Trans>
        </p>
        <InterestRateMessage className={styles.interestRateMessage} />
      </div>
    );
  };

  const handleButtonClick = () => {
    setScheduleOpen(true);
    trackEvent(AMPLITUDE_EVENTS.earlyRepaymentScheduleOpened, {
      Product: renderTitle(),
      'Number of repayments': summary.total_number_of_repayments || 'Undefined',
      'Max amount': formalOffer.max_amount,
      'Amount chosen': summary.total_credit_amount || 'Undefined',
      'Amount saved': summary.amount_saved || 'Undefined',
    });
  };

  return (
    <>
      <ButtonLink
        disabled={noSelectedOffer}
        className={styles.repaymentScheduleButton}
        onClick={handleButtonClick}
      >
        <Trans>See repayment schedule</Trans>
        <IconAngleRight height={16} width={16} />
      </ButtonLink>
      <Modal
        isOpen={scheduleOpen}
        onClose={() => setScheduleOpen(false)}
        closeIcon
        className={styles.repaymentScheduleModal}
        title={renderTitle()}
        subTitle={renderSubTitle()}
      >
        <RepaymentScheduleTable summary={summary} schedule={schedule} />
        {renderFooter()}
      </Modal>
    </>
  );
};
